/**
 * API Service — единственная точка доступа к backend.
 * Все запросы направляются на Render FastAPI backend.
 * При переходе на PostgreSQL достаточно обновить только backend — фронт не меняется.
 */

// Production: VITE_API_URL is set via .env.production (Render backend)
// Development: falls back to '/api' (local backend or Cloudflare Functions)
export const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** Публичный адрес Telegram-вебхука бэкенда (оплата Stars). */
export const TELEGRAM_WEBHOOK_URL = `${API_BASE.replace(/\/$/, '')}/telegram/webhook`;

let authToken = '';

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
};

// --- Response types ---

export interface Course {
  id: string;
  title: string;
  description?: string;
  duration?: string;        // minutes, e.g. "90"
  price: string;
  teacher_id: string;
  color: string;
  student_ids: string;
  student_count?: number;  // Real student count from group rosters
  location?: string;
  location_link?: string;
  is_active: string;
  created_at: string;
  monthly_price?: string;
  lesson_price?: string;
  lessons_per_week?: string;
  payment_type?: string;
}

export interface CourseAttendanceLesson {
  id: string;
  date: string;
  time: string;
  start_time: string;
  end_time: string;
  title: string;
  status: string;
}

export interface CourseAttendanceStudent {
  id: string;
  first_name: string;
  last_name: string;
  photo_url?: string;
}

export interface CourseAttendanceData {
  course_id: string;
  period: string;
  date_from: string | null;
  date_to: string | null;
  lessons: CourseAttendanceLesson[];
  students: CourseAttendanceStudent[];
  marks: Record<string, Record<string, string>>;
}

export interface Student {
  id: string;
  first_name: string;
  last_name: string;
  age?: string;
  birth_date?: string;
  parent_contact?: string;
  parent_name?: string;
  parent_relation?: string;
  phone?: string;
  telegram?: string;
  course_ids: string;
  start_date?: string;
  photo_url?: string;
  is_active: string;
  created_at: string;
}

export interface AttendanceRecord {
  id: string;
  lesson_id?: string;
  date: string;
  course_id: string;
  student_id: string;
  status: string;
  comment: string;
  marked_by: string;
  created_at: string;
}

export interface Payment {
  id: string;
  student_id: string;
  course_id: string;
  amount: string;
  payment_date: string;
  payment_type: string;
  payment_method?: string;
  comment: string;
  created_at: string;
}

export interface Achievement {
  id: string;
  student_id: string;
  title: string;
  icon: string;
  description: string;
  achieved_at: string;
  created_at: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: string;
    telegram_id: string;
    first_name: string;
    last_name?: string;
    username?: string;
    photo_url?: string;
    role: string;
    school_id?: string;
    timezone?: string;
    currency?: string;
    language_code?: string;
    language_manual?: boolean;
    needs_onboarding?: boolean;
    is_active: boolean;
  };
}

/**
 * Абонемент (пакет занятий) ученика.
 * lessons_left — сколько занятий ещё можно посетить до продления.
 */
export interface StudentSubscription {
  id: string;
  student_id: string;
  course_id: string;
  lessons_total: number;
  lessons_used: number;
  lessons_left: number;
  status: string;
  start_date: string;
  end_date: string;
  first_visit_date: string;
}

export interface StudentProfile {
  student: Student;
  courses: Course[];
  payments: Payment[];
  total_paid: number;
  achievements: Achievement[];
  // null — абонемента нет (оплата была без количества занятий)
  subscription?: StudentSubscription | null;
  attendance: {
    total_records: number;
    total_lessons: number;
    present: number;
    late: number;
    absent: number;
    excused: number;
    marked: number;
    unmarked: number;
    attendance_rate: number;
    last_visit: string;
    history: AttendanceRecord[];
  };
}

export interface SchoolMember {
  id: string;
  telegram_id: string;
  first_name: string;
  last_name: string;
  username: string;
  photo_url?: string;
  role: string;
}

/** Урок (группа) школы — для назначения учителям команды. */
export interface SchoolLesson {
  id: string;
  name: string;
  course_id: string;
  course_title: string;
  days: string;
  start_time: string;
  end_time: string;
  teacher: string;
  teacher_user_id: string;
}

export interface PaymentAlert {
  student_id: string;
  first_name: string;
  last_name: string;
  status: string;
  amount: string;
}

export interface LessonDashboardItem {
  id: string;
  course_id: string;
  date: string;
  time: string;
  start_time: string;
  end_time: string;
  title: string;
  status: string;
  homework: string;
  location: string;
  location_link: string;
  tz?: string;
  student_count: number;
  attendance_stats: {
    present: number;
    late: number;
    absent: number;
    trial: number;
    unmarked: number;
    total_marked: number;
  };
  unmarked_students: Array<{
    id: string;
    first_name: string;
    last_name: string;
  }>;
  color?: string;
}

export interface DashboardData {
  today: {
    date: string;
    lessons: LessonDashboardItem[];
    next_lesson: {
      id: string;
      course_id: string;
      title: string;
      time: string;
      color: string;
      location?: string;
      location_link?: string;
      status?: string;
    } | null;
    total_lessons: number;
    total_students: number;
    present: number;
    late: number;
    absent: number;
    unmarked: number;
    pending_payments: number;
    overdue_payments: number;
    payment_alerts: PaymentAlert[];
  };
  summary: {
    total_courses: number;
    total_students: number;
    total_payments: number;
  };
}

// --- Inbox types ---

export interface InboxItem {
  id: string;
  title: string;
  subtitle: string;
  priority: 'high' | 'medium' | 'low';
  action_label: string;
  action_url: string;
  student_id?: string;
  lesson_id?: string;
  // Lesson-specific fields
  lesson_time?: string;
  lesson_start?: string;
  lesson_end?: string;
  lesson_date?: string;
  lesson_tz?: string;
  student_count?: number;
  color?: string;
  time_until?: string;
  lesson_status?: 'upcoming' | 'current' | 'past';
  // Student-specific fields (trials, payments)
  photo_url?: string;
  first_name?: string;
  last_name?: string;
}

export interface InboxGroup {
  key: string;
  label: string;
  icon: string;
  items: InboxItem[];
}

export interface InboxData {
  date: string;
  groups: InboxGroup[];
  stats: {
    total: number;
    high_priority: number;
  };
}

// ─── Stats types (full analytics) ──────────────────────────────────────────

export interface StatsKpiItem {
  value: number;
  change: number | null;
}

export interface StatsData {
  period: string;
  period_label: string;
  date_from: string | null;
  date_to: string | null;
  kpi: {
    attendance: StatsKpiItem;
    revenue: StatsKpiItem & { new?: number; inactive?: number };
    students: StatsKpiItem & { new: number; inactive: number };
    lessons: StatsKpiItem;
  };
  attendance: {
    rate: number;
    present: number;
    late: number;
    absent: number;
    trial: number;
    daily: Array<{ date: string; attended: number; total: number }>;
    by_course: Array<{
      course_id: string;
      course_title: string;
      present: number;
      late: number;
      absent: number;
      total: number;
      rate: number;
    }>;
  };
  revenue: {
    total: number;
    by_course: Array<{ course_id: string; course_title: string; amount: number }>;
    monthly: Array<{ month: string; amount: number }>;
    by_method: Record<string, number>;
  };
  students: {
    active: number;
    new: number;
    inactive: number;
    retention_rate: number;
    attended: number;
    prev_attended: number;
  };
  total_lessons: number;
  top_students: Array<{
    rank: number;
    student_id: string;
    name: string;
    attended: number;
    total: number;
    rate: number;
  }>;
  courses: Array<{ id: string; title: string; color: string }>;
}

// ─── Owner panel (SaaS) types ──────────────────────────────────────────────

export interface OwnerSubscriptions {
  total: number;
  active: number;
  trial: number;
  expired: number;
  cancelled: number;
  past_due: number;
  pending: number;
  mrr: number;
  new_30d: number;
  cancelled_30d: number;
  conversion_free_to_paid: number;
  churn_rate: number;
  by_plan: { free: number; pro: number; school: number; business: number };
  monthly: Array<{
    month: string;
    new: number;
    cancelled: number;
    mrr: number;
  }>;
}

export interface SystemStats {
  users_total: number;
  users_active: number;
  users_new_7d: number;
  users_new_30d: number;
  users_by_role: Record<string, number>;
  solo_users: number;
  schools_total: number;
  teachers_total: number;
  admins_total: number;
  owner_school_total: number;
  subscriptions: OwnerSubscriptions;
  recent_users: Array<{
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    role: string;
    created_at: string;
  }>;
  google_sheets: boolean;
  postgres: boolean;
  api_status: string;
  backend_status: string;
}

export interface OwnerUser {
  id: string;
  telegram_id: string;
  first_name: string;
  last_name: string;
  username: string;
  role: string;
  role_label: string;
  school_id: string;
  school_name: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string;
  timezone: string;
}

export interface OwnerUserDetail extends OwnerUser {
  school: {
    id: string;
    name: string;
    owner_name: string;
    owner_telegram_id: string;
    created_at: string;
    timezone: string;
  } | null;
  data_counts: { courses: number; students: number; groups: number };
  subscription: {
    id: string | null;
    plan_id: string | null;
    status: string | null;
    start_date: string | null;
    end_date: string | null;
    payment_reference: string | null;
    history: SubscriptionEvent[];
  } | null;
}

export interface OwnerSchool {
  id: string;
  name: string;
  owner_name: string;
  owner_telegram_id: string;
  members_total: number;
  teachers_total: number;
  admins_total: number;
  students_total: number;
  groups_total: number;
  created_at: string;
  timezone: string;
  has_invite: boolean;
}

export interface OwnerSchoolDetail extends OwnerSchool {
  owner: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    telegram_id: string;
  } | null;
  invite_role: string;
  members: Array<{
    id: string;
    telegram_id: string;
    first_name: string;
    last_name: string;
    username: string;
    role: string;
    is_active: boolean;
    last_login_at: string;
  }>;
}

// ─── Billing (ЭТАП 4): plans / subscriptions / entitlements ────────────────

export interface Plan {
  id: string;
  name: string;
  description: string;
  price_usd: string;
  price_stars: string;
  billing_period: string;
  trial_days: string;
  is_active: boolean;
  sort_order: string;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriptionEvent {
  id: string;
  subscription_id: string;
  user_id: string;
  plan_id: string;
  event_type: string;
  detail: string;
  created_at: string;
}

export interface Subscription {
  id: string;
  user_id: string;
  user_name?: string;
  telegram_id?: string;
  plan_id: string;
  plan_name?: string;
  status: string;
  start_date: string;
  end_date: string;
  trial_end: string;
  cancelled_at: string;
  payment_reference: string;
  payment_provider: string;
  amount_paid: string;
  currency: string;
  renew_automatically: boolean;
  created_at: string;
  history?: SubscriptionEvent[];
}

export interface OwnerDiscount {
  id: string;
  name: string;
  code: string;
  percent: number;
  plan_id: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EntitlementSummary {
  is_system_owner: boolean;
  plan: {
    id: string;
    name: string;
    price_usd: string;
    price_stars: string;
    billing_period: string;
    trial_days: string;
  };
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
  subscription: { id: string; plan_id: string; status: string; end_date: string } | null;
}

// --- Request helpers ---

/** Ошибка API: HTTP-статус + человекочитаемый detail от FastAPI. */
export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail || `API Error (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/** Текст ошибки от бэкенда (например, «Достигнут лимит тарифа…») или запасной текст. */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.detail) return e.detail;
  return fallback;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const authHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    authHeaders['Authorization'] = `Bearer ${authToken}`;
  }

  const config: RequestInit = {
    method,
    headers: {
      ...authHeaders,
      ...headers,
    },
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let detail = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.detail === 'string') {
        detail = parsed.detail;
      } else if (parsed && Array.isArray(parsed.detail)) {
        detail = parsed.detail
          .map((d: { msg?: string }) => d?.msg || '')
          .filter(Boolean)
          .join('; ');
      }
    } catch {
      /* не JSON — оставляем сырой текст */
    }
    throw new ApiError(response.status, detail || `API Error (${response.status})`);
  }

  return response.json();
}

// --- API object ---

export const api = {
  // Auth token management
  setToken: (token: string) => { authToken = token; },
  getToken: () => authToken,

  // Auth
  login: (initData: string) => {
    // Сбрасываем authToken перед логином, чтобы случайно не отправить
    // старый токен. Сам api.login() не защищён — он создаёт новый токен.
    // Токен хранится ТОЛЬКО в памяти (не в localStorage) — см. AuthContext.
    const oldToken = authToken;
    authToken = '';
    return request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { init_data: initData },
    }).finally(() => {
      // Восстанавливаем токен на случай, если это не первый логин
      // (например, при retry после неудачной попытки).
      // Успешный логин сам вызовет api.setToken() и перезапишет.
      authToken = oldToken;
    });
  },
  getMe: () => request<LoginResponse['user']>('/auth/me'),

  // Update own profile (any role) — timezone and/or currency
  updateUserTimezone: (timezone: string) =>
    request<LoginResponse['user']>('/auth/me', {
      method: 'PUT',
      body: { timezone },
    }),
  updateUserCurrency: (currency: string) =>
    request<LoginResponse['user']>('/auth/me', {
      method: 'PUT',
      body: { currency },
    }),
  updateUserLanguage: (language: string) =>
    request<LoginResponse['user']>('/auth/me', {
      method: 'PUT',
      body: { language },
    }),

  // School home timezone (owner_school)
  updateSchoolTimezone: (timezone: string) =>
    request<{ status: string; school: any }>('/school/timezone', {
      method: 'PUT',
      body: { timezone },
    }),

  // Onboarding (first login)
  completeOnboarding: (choice: 'solo' | 'team', schoolName?: string, timezone?: string) =>
    request<{
      access_token: string;
      token_type: string;
      user: LoginResponse['user'];
      school?: { id: string; name: string; owner_user_id: string } | null;
    }>('/auth/onboarding', {
      method: 'POST',
      body: { choice, school_name: schoolName, timezone },
    }),

  // Upgrade user → owner_school (from Account)
  upgradeToSchoolOwner: (schoolName: string, timezone?: string) =>
    request<{
      access_token: string;
      token_type: string;
      user: LoginResponse['user'];
      school: { id: string; name: string; owner_user_id: string };
    }>('/auth/upgrade', {
      method: 'POST',
      body: { school_name: schoolName, timezone },
    }),

  // School team panel (owner_school)
  getMySchool: () => request<{ school: any; members: SchoolMember[] }>('/school'),
  searchUsersForSchool: (q: string) =>
    request<{ users: SchoolMember[] }>(`/school/search?q=${encodeURIComponent(q)}`),
  addSchoolMember: (telegramId: number, role: 'teacher' | 'admin') =>
    request<{ status: string }>('/school/members', {
      method: 'POST',
      body: { telegram_id: telegramId, role },
    }),
  setSchoolMemberRole: (userId: string, role: 'teacher' | 'admin') =>
    request<{ status: string }>(`/school/members/${userId}/role`, {
      method: 'PUT',
      body: { role },
    }),
  removeSchoolMember: (userId: string) =>
    request<{ status: string }>(`/school/members/${userId}`, { method: 'DELETE' }),
  generateInvite: (role: 'teacher' | 'admin' = 'teacher') =>
    request<{ token: string; link: string; role: string }>('/school/invite', {
      method: 'POST',
      body: { role },
    }),

  // Уроки школы и назначение их учителям команды (владелец/админ школы)
  getSchoolLessons: () => request<{ lessons: SchoolLesson[] }>('/school/lessons'),
  setMemberLessonAssignments: (userId: string, groupIds: string[]) =>
    request<{ status: string; user_id: string; group_ids: string[]; assigned: number; cleared: number }>(
      `/school/members/${userId}/assignments`,
      { method: 'PUT', body: { group_ids: groupIds } },
    ),
  joinSchool: (token: string) =>
    request<{
      status: string;
      already_member: boolean;
      access_token?: string;
      user?: LoginResponse['user'];
      school?: { id: string; name: string };
    }>('/school/join', {
      method: 'POST',
      body: { token },
    }),

  // Health
  health: () => request<{ status: string; version: string }>('/health'),

  // Courses
  getCourses: (archived?: boolean) => {
    const params = new URLSearchParams();
    if (archived) params.set('archived', 'true');
    const qs = params.toString();
    return request<{ courses: Course[] }>(`/courses${qs ? `?${qs}` : ''}`);
  },
  getCourse: (id: string) => request<Course>(`/courses/${id}`),
  getCourseAttendance: (courseId: string, period: string = 'all') => {
    const params = new URLSearchParams();
    if (period && period !== 'all') params.set('period', period);
    const qs = params.toString();
    return request<CourseAttendanceData>(`/courses/${courseId}/attendance${qs ? `?${qs}` : ''}`);
  },
  createCourse: (data: Partial<Course>) => request<Course>('/courses', { method: 'POST', body: data }),
  updateCourse: (id: string, data: Partial<Course>) =>
    request<{ status: string }>(`/courses/${id}`, { method: 'PUT', body: data }),
  deleteCourse: (id: string) => request<{ status: string }>(`/courses/${id}`, { method: 'DELETE' }),

  // Students
  getStudents: (courseId?: string, search?: string, archived?: boolean) => {
    const params = new URLSearchParams();
    if (courseId) params.set('course_id', courseId);
    if (search) params.set('search', search);
    if (archived) params.set('archived', 'true');
    const qs = params.toString();
    return request<{ students: Student[] }>(`/students${qs ? `?${qs}` : ''}`);
  },
  getStudent: (id: string) => request<Student>(`/students/${id}`),
  createStudent: (data: Partial<Student>) =>
    request<Student>('/students', { method: 'POST', body: data }),
  updateStudent: (id: string, data: Partial<Student>) =>
    request<{ status: string }>(`/students/${id}`, { method: 'PUT', body: data }),
  deleteStudent: (id: string) => request<{ status: string }>(`/students/${id}`, { method: 'DELETE' }),

  // Enrollment
  enrollStudent: (courseId: string, studentId: string) =>
    request<{ status: string }>(`/courses/${courseId}/enroll`, {
      method: 'POST',
      body: { student_id: studentId },
    }),
  unenrollStudent: (courseId: string, studentId: string) =>
    request<{ status: string }>(`/courses/${courseId}/enroll/${studentId}`, {
      method: 'DELETE',
    }),
  searchStudents: (query: string) =>
    request<{ students: Student[] }>(`/students?search=${encodeURIComponent(query)}`),

  // Attendance
  getAttendance: (courseId?: string, date?: string, lessonId?: string) => {
    const params = new URLSearchParams();
    if (courseId) params.set('course_id', courseId);
    if (date) params.set('date', date);
    if (lessonId) params.set('lesson_id', lessonId);
    const qs = params.toString();
    return request<{ attendance: AttendanceRecord[] }>(`/attendance${qs ? `?${qs}` : ''}`);
  },
  getStudentAttendance: (studentId: string) =>
    request<{ attendance: AttendanceRecord[] }>(`/attendance/student/${studentId}`),
  markAttendance: (data: Partial<AttendanceRecord>) =>
    request<AttendanceRecord>('/attendance', { method: 'POST', body: data }),
  updateAttendance: (id: string, data: Partial<AttendanceRecord>) =>
    request<{ status: string }>(`/attendance/${id}`, { method: 'PUT', body: data }),
  deleteAttendance: (id: string) =>
    request<{ status: string }>(`/attendance/${id}`, { method: 'DELETE' }),

  // Payments — journal-based
  getPayments: () =>
    request<{ payments: Payment[] }>('/payments'),
  getStudentPayments: (studentId: string) =>
    request<{ payments: Payment[] }>(`/payments/student/${studentId}`),

  createPayment: (data: {
    student_id: string;
    course_id: string;
    amount: number;
    payment_date?: string;
    payment_type?: string;
    payment_method?: string;
    comment?: string;
    // Сколько занятий входит в абонемент ученика (необязательно).
    lessons_per_month?: number;
  }) => request<Payment>('/payments', { method: 'POST', body: data }),
  updatePayment: (id: string, data: Partial<Payment>) =>
    request<{ status: string }>(`/payments/${id}`, { method: 'PUT', body: data }),

  // Achievements
  getAchievements: (studentId?: string) =>
    request<{ achievements: Achievement[] }>(`/achievements${studentId ? `?student_id=${studentId}` : ''}`),
  createAchievement: (data: Partial<Achievement>) =>
    request<Achievement>('/achievements', { method: 'POST', body: data }),
  deleteAchievement: (id: string) =>
    request<{ status: string }>(`/achievements/${id}`, { method: 'DELETE' }),

  // Student Profile
  getStudentProfile: (id: string) => request<StudentProfile>(`/students/${id}/profile`),

  // Абонемент ученика (пакет занятий): просмотр и ручная правка
  getStudentSubscription: (studentId: string) =>
    request<{ subscription: StudentSubscription | null }>(`/students/${studentId}/subscription`),
  adjustStudentLessons: (studentId: string, delta: number) =>
    request<{ status: string; subscription: StudentSubscription }>(
      `/students/${studentId}/subscription/lessons`,
      { method: 'POST', body: { delta } },
    ),

  // Dashboard
  getDashboard: () => request<DashboardData>('/dashboard'),

  // Inbox — daily action stream
  getInbox: () => request<InboxData>('/inbox'),

  // Groups (Course → Group → Lesson)
  getGroups: (courseId?: string, archived?: boolean) => {
    const params = new URLSearchParams();
    if (courseId) params.set('course_id', courseId);
    if (archived) params.set('archived', 'true');
    const qs = params.toString();
    return request<{ groups: any[] }>(`/groups${qs ? `?${qs}` : ''}`);
  },
  getGroup: (id: string) => request<any>(`/groups/${id}`),
  createGroup: (data: any) => request<any>('/groups', { method: 'POST', body: data }),
  updateGroup: (id: string, data: any) =>
    request<{ status: string }>(`/groups/${id}`, { method: 'PUT', body: data }),
  deleteGroup: (id: string) => request<{ status: string }>(`/groups/${id}`, { method: 'DELETE' }),
  addStudentToGroup: (groupId: string, studentId: string) =>
    request<{ status: string }>(`/groups/${groupId}/students`, {
      method: 'POST', body: { student_id: studentId },
    }),
  removeStudentFromGroup: (groupId: string, studentId: string) =>
    request<{ status: string }>(`/groups/${groupId}/students/${studentId}`, {
      method: 'DELETE',
    }),
  getCourseGroups: (courseId: string) =>
    request<{ groups: any[] }>(`/courses/${courseId}/groups`),

  // Lessons (new lesson-centric API)
  getLesson: (id: string) => request<any>(`/lessons/${id}`),
  getTodayLessons: () => request<{ lessons: any[] }>('/lessons/today'),
  getLessons: (date?: string, courseId?: string, groupId?: string) => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (courseId) params.set('course_id', courseId);
    if (groupId) params.set('group_id', groupId);
    const qs = params.toString();
    return request<{ lessons: any[] }>(`/lessons${qs ? `?${qs}` : ''}`);
  },
  ensureLesson: (courseId: string, date: string, groupId?: string) =>
    request<any>('/lessons/ensure', {
      method: 'POST',
      body: groupId ? { group_id: groupId, date } : { course_id: courseId, date },
    }),
  createLesson: (data: any) => request<any>('/lessons', { method: 'POST', body: data }),
  updateLesson: (id: string, data: any) =>
    request<{ status: string }>(`/lessons/${id}`, { method: 'PUT', body: data }),

  // Users (admin)
  listUsers: () => request<{ users: any[] }>('/auth/admin/users'),
  setUserRole: (telegramId: number, role: string) =>
    request<{ status: string }>(`/auth/admin/users/${telegramId}/role`, {
      method: 'PUT',
      body: { role },
    }),

  // System / Owner panel (owner only)
  getSystemStats: () => request<SystemStats>('/system/stats'),
  getOwnerUsers: (search?: string) => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<{ users: OwnerUser[] }>(`/system/users${qs}`);
  },
  getOwnerUser: (id: string) => request<OwnerUserDetail>(`/system/users/${id}`),
  setOwnerUserActive: (id: string, isActive: boolean) =>
    request<{ status: string; user_id: string; is_active: boolean }>(
      `/system/users/${id}/active`,
      { method: 'PUT', body: { is_active: isActive } },
    ),
  setOwnerUserRole: (id: string, role: string) =>
    request<{ status: string; user_id: string; role: string }>(
      `/system/users/${id}/role`,
      { method: 'PUT', body: { role } },
    ),
  getOwnerSchools: () => request<{ schools: OwnerSchool[] }>('/system/schools'),
  getOwnerSchool: (id: string) => request<OwnerSchoolDetail>(`/system/schools/${id}`),

  // Billing / plans (owner)
  getOwnerPlans: () => request<{ plans: Plan[] }>('/system/plans'),
  createOwnerPlan: (data: Partial<Plan> & { id: string }) =>
    request<Plan>('/system/plans', { method: 'POST', body: data }),
  updateOwnerPlan: (id: string, data: Partial<Plan>) =>
    request<Plan>(`/system/plans/${id}`, { method: 'PUT', body: data }),
  getOwnerDiscounts: () => request<{ discounts: OwnerDiscount[] }>('/system/discounts'),
  createOwnerDiscount: (data: Partial<OwnerDiscount> & { id: string }) =>
    request<OwnerDiscount>('/system/discounts', { method: 'POST', body: data }),
  updateOwnerDiscount: (id: string, data: Partial<OwnerDiscount>) =>
    request<OwnerDiscount>(`/system/discounts/${id}`, { method: 'PUT', body: data }),
  deleteOwnerDiscount: (id: string) =>
    request<{ status: string; discount_id: string }>(`/system/discounts/${id}`, { method: 'DELETE' }),
  getOwnerSubscriptions: () =>
    request<{ subscriptions: Subscription[] }>('/system/subscriptions'),
  getOwnerSubscription: (id: string) =>
    request<Subscription>(`/system/subscriptions/${id}`),
  createOwnerSubscription: (data: {
    user_id: string;
    plan_id: string;
    payment_reference?: string;
    payment_provider?: string;
    amount_paid?: string;
    currency?: string;
  }) => request<Subscription>('/system/subscriptions', { method: 'POST', body: data }),
  cancelOwnerSubscription: (id: string) =>
    request<{ status: string; subscription_id: string }>(
      `/system/subscriptions/${id}/cancel`,
      { method: 'POST', body: {} },
    ),
  registerTelegramWebhook: (url: string, dropPending?: boolean) =>
    request<{ status: string; setWebhook: unknown; webhook_info: unknown }>(
      '/system/telegram/webhook',
      { method: 'POST', body: { url, drop_pending: dropPending ?? false } },
    ),

  // Billing (user-facing)
  getMyBillingPlan: () => request<EntitlementSummary>('/billing/plan'),
  getMyBillingSubscription: () =>
    request<{
      subscription: Subscription | null;
      history: SubscriptionEvent[];
      available_plans: Plan[];
    }>('/billing/subscription'),
  startMyTrial: (planId: string) =>
    request<{ subscription: Subscription; created: boolean }>('/billing/trial', {
      method: 'POST',
      body: { plan_id: planId },
    }),
  createInvoice: (planId: string, promoCode?: string) =>
    request<{
      subscription: Subscription;
      invoice_link: string;
      payload: string;
      amount_stars: number;
      discount?: { percent: number; name: string } | null;
    }>('/billing/invoice', {
      method: 'POST',
      body: { plan_id: planId, promo_code: promoCode || null },
    }),
  applyPromo: (planId: string, promoCode: string) =>
    request<{
      valid: boolean;
      percent: number;
      name: string;
      base_stars: number;
      amount_stars: number;
    }>('/billing/apply-promo', {
      method: 'POST',
      body: { plan_id: planId, promo_code: promoCode },
    }),
  cancelMySubscription: () =>
    request<{ status: string; subscription_id: string; access_until: string }>(
      '/billing/subscription/cancel',
      { method: 'POST' },
    ),

  // Stats — aggregated analytics with course filter
  getStats: (period: string = 'month', courseId?: string) => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (courseId) params.set('course_id', courseId);
    return request<StatsData>(`/stats?${params.toString()}`);
  },
};

export default api;
